import React, { useRef, useState } from "react";
import {
  IconButtonProps,
  Popover,
  ToggleButton as MuiToggleButton,
} from "@mui/material";
import styled from "@emotion/styled";
import Calendar from "@icons/myicons/calendar-days.svg?react";
import {
  DateRangePicker,
  DateRangePickerProps,
} from "../DateRangePicker/DateRangePicker";

const ToggleButton = styled(MuiToggleButton)`
  font-size: 14px;
  border: none;
  font-weight: normal;
  color: ${({ theme }) => theme.palette.text.secondary};
  padding: ${({ theme }) => theme.spacing(2, 3)};
  text-transform: unset;
  border: 1px solid ${({ theme }) => theme.palette.border.primary};
  box-shadow: ${({ theme }) => theme.shadows[1]} !important;

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.icon.active};
    background-color: ${({ theme }) => theme.palette.background.neutralDark};
    font-weight: 500;
  }
`;

interface Props
  extends Omit<IconButtonProps, "onChange" | "onReset">,
    Omit<DateRangePickerProps, "onChange"> {
  defaultFrom?: Date;
  onChange: (from: Date, to?: Date) => void;
  onOpen?: () => void;
  from?: Date;
  to?: Date;
  disablePortal?: boolean;
  value?: IntervalType;
  active?: boolean;
}

export const DateRangeButton = ({
  onOpen,
  onChange,
  onReset,
  defaultFrom,
  defaultTo,
  from,
  to,
  disablePortal,
  value = "custom",
  active = false,
  ...rest
}: Props) => {
  const [open, setOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<
    { from?: Date; to?: Date } | undefined
  >();

  const ref = useRef<HTMLButtonElement>(null);

  const handleDateChange = (from?: Date, to?: Date) => {
    if (!from) return;
    setTempDateRange({ from, to });
  };

  const handleApply = () => {
    if (tempDateRange && tempDateRange.from) {
      onChange(tempDateRange.from, tempDateRange.to);
      setOpen(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    onOpen && onOpen();
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        disablePortal={disablePortal}
        slotProps={{
          paper: {
            className: "range-date-picker-paper",
          },
          root: {
            slotProps: {
              backdrop: {
                className: "range-date-picker-backdrop",
              },
            },
          },
        }}
      >
        <DateRangePicker
          onChange={handleDateChange}
          onApply={handleApply}
          onReset={onReset}
          defaultFrom={defaultFrom}
          defaultTo={defaultTo}
          from={from}
          to={to}
          {...rest}
        />
      </Popover>
      <ToggleButton
        ref={ref}
        key="start-datepicker-action"
        size="small"
        value={value}
        selected={active || open}
        onClick={handleOpen}
      >
        <Calendar width="20px" height="20px" />
      </ToggleButton>
    </>
  );
};
